import React from 'react';
import { motion } from 'framer-motion';
import { Database, Zap, Cloud, Globe, GitBranch, Server, Code } from 'lucide-react';
import { cn } from '../../lib/utils';

const InternshipTechStack = () => {
  const features = [
    { icon: Code, label: 'VUE.JS / PHP', color: 'from-green-500 to-emerald-500' },
    { icon: Zap, label: 'Veeam REST API', color: 'from-yellow-500 to-orange-500' },
    { icon: Database, label: 'MYSQL Server', color: 'from-blue-500 to-cyan-500' },
    { icon: Globe, label: 'Tailwind UI', color: 'from-purple-500 to-pink-500' },
    { icon: Cloud, label: 'Azure Data Studio', color: 'from-cyan-500 to-blue-500' },
    { icon: GitBranch, label: 'Git / Gitlab', color: 'from-gray-500 to-slate-500' }
  ];

  return (
    <div className="w-full py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-[#0a0a0a] via-black to-[#0a0a0a] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16 lg:mb-20"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6"
          >
            <Server className="w-4 h-4 text-orange-500" />
            <span className="text-xs sm:text-sm font-silkscreen text-white/80">Internship Tech Stack</span>
          </motion.div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-silkscreen mb-4">
            <span className="bg-gradient-to-r from-white via-orange-500 to-white bg-clip-text text-transparent">
              Built With Purpose
            </span>
          </h2>
          <p className="text-white/60 text-sm sm:text-base max-w-2xl mx-auto">
            Modern automation system with Vue.js frontend, Veeam API integration and SQL Server database
          </p>
        </motion.div>

        {/* Main Animation Container */}
        <div className="relative">
          {/* Background Glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 via-green-500/10 to-orange-500/10 blur-3xl" />

          <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Side - Combined Animations */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative aspect-square max-w-lg mx-auto w-full"
            >
              {/* SVG Layer for Paths and Traveling Lights */}
              <div className="absolute inset-0 flex items-center justify-center">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 400 400"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    {/* Gradients for lights */}
                    <radialGradient id="green-grad" fx="1">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="transparent" />
                    </radialGradient>
                    <radialGradient id="orange-grad" fx="1">
                      <stop offset="0%" stopColor="#f97316" />
                      <stop offset="100%" stopColor="transparent" />
                    </radialGradient>
                    <radialGradient id="blue-grad" fx="1">
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="100%" stopColor="transparent" />
                    </radialGradient>
                    <radialGradient id="purple-grad" fx="1">
                      <stop offset="0%" stopColor="#a855f7" />
                      <stop offset="100%" stopColor="transparent" />
                    </radialGradient>

                    {/* Masks for light paths */}
                    <mask id="stage-mask-1">
                      <path d="M 60 20 v 30 q 0 10 10 10 h 120 q 10 0 10 10 v 20" strokeWidth="1" stroke="white" />
                    </mask>
                    <mask id="stage-mask-2">
                      <path d="M 150 20 v 20 q 0 10 10 10 h 30 q 10 0 10 10 v 30" strokeWidth="1" stroke="white" />
                    </mask>
                    <mask id="stage-mask-3">
                      <path d="M 250 20 v 20 q 0 10 -10 10 h -30 q -10 0 -10 10 v 30" strokeWidth="1" stroke="white" />
                    </mask>
                    <mask id="stage-mask-4">
                      <path d="M 340 20 v 30 q 0 10 -10 10 h -120 q -10 0 -10 10 v 20" strokeWidth="1" stroke="white" />
                    </mask>
                  </defs>

                  {/* Static Paths */}
                  <g stroke="currentColor" className="text-white/20" fill="none" strokeWidth="0.75">
                    <path d="M 60 20 v 30 q 0 10 10 10 h 120 q 10 0 10 10 v 20" strokeDasharray="2 2">
                      <animate attributeName="stroke-dashoffset" from="0" to="4" dur="0.5s" repeatCount="indefinite" />
                    </path>
                    <path d="M 150 20 v 20 q 0 10 10 10 h 30 q 10 0 10 10 v 30" strokeDasharray="2 2">
                      <animate attributeName="stroke-dashoffset" from="0" to="4" dur="0.5s" repeatCount="indefinite" />
                    </path>
                    <path d="M 250 20 v 20 q 0 10 -10 10 h -30 q -10 0 -10 10 v 30" strokeDasharray="2 2">
                      <animate attributeName="stroke-dashoffset" from="0" to="4" dur="0.5s" repeatCount="indefinite" />
                    </path>
                    <path d="M 340 20 v 30 q 0 10 -10 10 h -120 q -10 0 -10 10 v 20" strokeDasharray="2 2">
                      <animate attributeName="stroke-dashoffset" from="0" to="4" dur="0.5s" repeatCount="indefinite" />
                    </path>
                  </g>

                  {/* Animated Lights */}
                  <g mask="url(#stage-mask-1)">
                    <circle className="animate-path-1" cx="0" cy="0" r="4" fill="url(#green-grad)" />
                  </g>
                  <g mask="url(#stage-mask-2)">
                    <circle className="animate-path-2" cx="0" cy="0" r="4" fill="url(#orange-grad)" />
                  </g>
                  <g mask="url(#stage-mask-3)">
                    <circle className="animate-path-3" cx="0" cy="0" r="4" fill="url(#blue-grad)" />
                  </g>
                  <g mask="url(#stage-mask-4)">
                    <circle className="animate-path-4" cx="0" cy="0" r="4" fill="url(#purple-grad)" />
                  </g>

                  {/* Tech Labels */}
                  <g className="text-white">
                    <rect x="25" y="10" width="70" height="20" rx="10" fill="#18181B" stroke="currentColor" strokeWidth="0.5" className="text-green-500/50" />
                    <text x="60" y="23" textAnchor="middle" className="fill-green-500" fontSize="7" fontWeight="bold">VUE.JS</text>

                    <rect x="115" y="10" width="70" height="20" rx="10" fill="#18181B" stroke="currentColor" strokeWidth="0.5" className="text-orange-500/50" />
                    <text x="150" y="23" textAnchor="middle" className="fill-orange-500" fontSize="7" fontWeight="bold">VEEAM</text>

                    <rect x="215" y="10" width="70" height="20" rx="10" fill="#18181B" stroke="currentColor" strokeWidth="0.5" className="text-blue-500/50" />
                    <text x="250" y="23" textAnchor="middle" className="fill-blue-500" fontSize="7" fontWeight="bold">SQL</text>

                    <rect x="305" y="10" width="70" height="20" rx="10" fill="#18181B" stroke="currentColor" strokeWidth="0.5" className="text-purple-500/50" />
                    <text x="340" y="23" textAnchor="middle" className="fill-purple-500" fontSize="7" fontWeight="bold">AZURE</text>
                  </g>
                </svg>
              </div>

              {/* Center Server Icon */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-green-500 rounded-2xl blur-xl opacity-50" />
                  <div className="relative bg-gradient-to-br from-orange-500/20 to-green-500/20 backdrop-blur-sm border border-white/20 rounded-2xl p-6 sm:p-8">
                    <Server className="w-12 h-12 sm:w-16 sm:h-16 text-white drop-shadow-glow" />
                  </div>
                </motion.div>
              </div>

              {/* Orbiting Elements */}
              {features.map((feature, index) => {
                return (
                  <motion.div
                    key={index}
                    className="absolute top-1/2 left-1/2 pointer-events-none"
                    style={{
                      offsetPath: 'path("M 0 0 m -150, 0 a 150,150 0 1,0 300,0 a 150,150 0 1,0 -300,0")',
                      offsetDistance: '0%',
                      willChange: 'offset-distance',
                    }}
                    animate={{
                      offsetDistance: ['0%', '100%']
                    }}
                    transition={{
                      duration: 20,
                      repeat: Infinity,
                      ease: 'linear',
                      delay: (index / features.length) * 20
                    }}
                  >
                    <div className="relative -translate-x-1/2 -translate-y-1/2">
                      <div className={cn(
                        "absolute inset-0 bg-gradient-to-r rounded-xl blur-lg opacity-50",
                        feature.color
                      )} />
                      <div className="relative bg-black/80 backdrop-blur-sm border border-white/10 rounded-xl p-3 sm:p-4">
                        <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                    </div>
                  </motion.div>
                );
              })}

              {/* Connecting Lines Animation */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
                <motion.circle
                  cx="50%"
                  cy="50%"
                  r="150"
                  fill="none"
                  stroke="url(#gradient-stage)"
                  strokeWidth="2"
                  strokeDasharray="10 5"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.3 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, ease: "easeInOut" }}
                />
                <motion.circle
                  cx="50%"
                  cy="50%"
                  r="180"
                  fill="none"
                  stroke="url(#gradient2-stage)"
                  strokeWidth="1"
                  strokeDasharray="5 10"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.2 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2.5, ease: "easeInOut", delay: 0.2 }}
                />
                <defs>
                  <linearGradient id="gradient-stage" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#f97316" />
                    <stop offset="50%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#f97316" />
                  </linearGradient>
                  <linearGradient id="gradient2-stage" x1="100%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="50%" stopColor="#f97316" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                </defs>
              </svg>
            </motion.div>

            {/* Right Side - Features List */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-4 sm:space-y-6"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ x: 10 }}
                  className="group"
                >
                  <div className="relative">
                    <div className={cn(
                      "absolute inset-0 bg-gradient-to-r rounded-xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity",
                      feature.color
                    )} />
                    <div className="relative flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-5 group-hover:border-white/30 transition-all">
                      <div className={cn(
                        "p-2 sm:p-3 bg-gradient-to-br rounded-lg",
                        feature.color
                      )}>
                        <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-silkscreen text-sm sm:text-base text-white ">
                          {feature.label}
                        </h3>
                        <p className="text-xs sm:text-sm text-white/60 mt-1">
                          Production-ready technology
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternshipTechStack;
